import React, { useRef, useEffect, useState, useMemo } from 'react';

import { StepsNavigationLegacy } from '@/widgets/ServiceFormTemplate';

import { ConditionView } from '@/shared/ui';

import { ServiceFeedback } from '@/components/Forms/ServiceFeedback';

import { SERVICE_CODES } from '@/lib/consts';

import { DocumentTypeCode } from '@/api/types';

import { allAllowedToChoosePassportDocumentTypeCodes } from '../../../../../components/Forms/constants';
import { HeaderWithHomeLinkButton } from '../../../../../components/Forms/HeaderWithHomeLinkButton';
import { ServiceInformer } from '../../../../../components/Forms/ServiceInformer';
import { ServiceInformerContainer } from '../../../../../components/Forms/ServiceInformer/ServiceInformerContainer';
import { ServiceInformerType } from '../../../../../components/Forms/ServiceInformer/types';
import { ServiceNames, StepsNavigationName } from '../../../../../config/services';
import { stepsNavigationDataFactory } from '../../../../../helpers/stepsNavigationDataFactory';

import FormSteps from './Steps/StepInitial';

import './WeaponStorageCarrying.scss';
import '../../../../../containers/StyleSettings/themes/default/layout.scss';

const StepsNavigationData = stepsNavigationDataFactory([
  StepsNavigationName.serviceDescription,
  StepsNavigationName.weaponData,
  StepsNavigationName.personalAndPassport,
  StepsNavigationName.currentPlaceOfResidence,
  StepsNavigationName.insurance,
  StepsNavigationName.technicalCondition,
  StepsNavigationName.additionalDocument,
  StepsNavigationName.confirmationOfApplication,
  StepsNavigationName.signUp,
  StepsNavigationName.payment,
  StepsNavigationName.result,
]);

export const disallowedDocumentTypeCodesForAdditionalDocumentStep = [
  ...allAllowedToChoosePassportDocumentTypeCodes,
  DocumentTypeCode.INSURANCE_POLICY,
  DocumentTypeCode.TECHNICAL_CONDITION,
  DocumentTypeCode.PAYMENT_REFERENCE,
];

export const currentServiceCode = SERVICE_CODES.STORAGE_CARRYING;
export const serviceName = ServiceNames[currentServiceCode];
export const totalSteps = StepsNavigationData.length;
export const insuranceStepNumber = StepsNavigationData.find(
  (stepNavigation) => stepNavigation.name === StepsNavigationName.insurance,
)?.step;
export const stepNumberToPay = StepsNavigationData.length - 1;
export const stepNumberToSubmitForm = StepsNavigationData.length - 2;
export const stepNumberToDownloadPdf = StepsNavigationData.length - 3;

export const WeaponStorageCarring: React.FC = () => {
  const htmlRef = useRef<HTMLElement>(null);
  const [myObjects, setMyObjects] = useState(StepsNavigationData);
  const currentStep = useMemo(() => myObjects.find((step) => step.active), [myObjects]);
  const isLastStep: boolean = currentStep === myObjects[myObjects.length - 1];
  const [currentRequestId, setCurrentRequestId] = useState<string>('');
  const isPaymentDetailsInformerActive = useMemo(
    () => currentStep?.step === StepsNavigationData[0].step || currentStep?.step === stepNumberToPay,
    [currentStep],
  );
  const isClueInformerActive = useMemo(
    () => currentStep?.step === StepsNavigationData[0].step || currentStep?.step === StepsNavigationData.at(-1)?.step,
    [currentStep],
  );
  const isValidityPeriodViolationInformerActive = useMemo(() => currentStep?.step === 2, [currentStep]);
  const isPlaceOfResidenceInformerActive = useMemo(() => currentStep?.step === 4, [currentStep]);
  const isInsuranceDataInformerActive = useMemo(() => currentStep?.step === 5, [currentStep]);
  const isTechnicalConditionDetailsInformerActive = useMemo(() => currentStep?.step === 6, [currentStep]);
  const isTechnicalConditionDocumentDataInformerActive = useMemo(() => currentStep?.step === 6, [currentStep]);
  const isQrCodeInformerActive = useMemo(() => currentStep?.step === 9, [currentStep]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const stepToActivate = Number(htmlRef.current?.querySelector('div[data-step]')?.getAttribute('data-step'));
      const newData = JSON.parse(JSON.stringify(myObjects)).map((step: (typeof StepsNavigationData)[0]) => {
        if (step.step === stepToActivate) {
          step.active = true;
          step.completed = false;
        } else {
          if (step.step < stepToActivate) {
            step.completed = true;
          }
          if (step.active) {
            step.active = false;
          }
        }

        return step;
      });

      setMyObjects(newData);
    });
    htmlRef.current && observer.observe(htmlRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleReceiveRequestId = (requestId: string): void => {
    setCurrentRequestId(requestId);
  };

  return (
    <section className="section-block-medium section-light">
      <div className="container container-with-aside_margin-bottom-sm">
        <HeaderWithHomeLinkButton header={serviceName} />
      </div>
      <div className="wpp-container container container-with-two-asides container-with-aside_gap-md">
        <aside className="container-with-aside__aside">
          <StepsNavigationLegacy navigationData={myObjects} />
        </aside>
        <div>
          <section
            className="container-with-aside__section container-with-aside__section_width-md max-width-md"
            ref={htmlRef}
          >
            <FormSteps onReceiveRequestId={handleReceiveRequestId} />
          </section>

          <ConditionView if={isLastStep && !!currentRequestId}>
            <ServiceFeedback requestId={currentRequestId} />
          </ConditionView>
        </div>
        <ServiceInformerContainer>
          {isQrCodeInformerActive && <ServiceInformer type={ServiceInformerType.QR_CODE1} />}
          {isPaymentDetailsInformerActive && (
            <ServiceInformer type={ServiceInformerType.PAYMENT_DETAILS} serviceCode={currentServiceCode} />
          )}
          {isClueInformerActive && <ServiceInformer type={ServiceInformerType.CLUE} />}
          {isValidityPeriodViolationInformerActive && (
            <ServiceInformer type={ServiceInformerType.VALIDITY_PERIOD_VIOLATION} />
          )}
          {isPlaceOfResidenceInformerActive && <ServiceInformer type={ServiceInformerType.PLACE_OF_RESIDENCE} />}
          {isInsuranceDataInformerActive && <ServiceInformer type={ServiceInformerType.INSURANCE_DATA} />}
          {isTechnicalConditionDetailsInformerActive && (
            <ServiceInformer type={ServiceInformerType.TECHNICAL_CONDITION_DETAILS} />
          )}
          {isTechnicalConditionDocumentDataInformerActive && (
            <ServiceInformer type={ServiceInformerType.TECHNICAL_CONDITION_DOCUMENT_DATA} />
          )}
          <ServiceInformer type={ServiceInformerType.CONTACT_CENTER} />
        </ServiceInformerContainer>
      </div>
    </section>
  );
};
